import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoDirective } from '@jsverse/transloco';
import { errorIcon } from '@ld.consumer-portal/data-uri';
import { BrowserService, PlatformService, LoggingService, StatusBarService} from '@ld.consumer-portal/utils';
import { interval, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'ld-esign-modal',
  imports: [MatIconModule, MatProgressSpinnerModule, TranslocoDirective],
  templateUrl: './esign-modal.component.html',
  styleUrl: './esign-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsignModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly platformService = inject(PlatformService);

  protected readonly errorIcon = errorIcon;
  private readonly browserService = inject(BrowserService);
  private readonly loggingService = inject(LoggingService);
  private readonly statusBarService = inject(StatusBarService);

  readonly esignUrl = input.required<string>();

  readonly accessToken = input<string | undefined>();

  readonly documentName = input<string>('Document');

  readonly complete = output<void>();

  readonly cancel = output<void>();

  readonly error = output<string>();

  protected readonly isLoading = signal(true);

  protected readonly hasError = signal(false);

  protected readonly errorMessage = signal<string | null>(null);

  protected readonly usingInAppBrowser = signal(false);

  private embedHost = '';

  private readonly activityWatcher$ = new Subject<Event>();
  private readonly destroy$ = new Subject<void>();

  private readonly esignForm = viewChild<ElementRef<HTMLFormElement>>('esignForm');

  protected readonly isDocutechFlow = computed(() => !!this.accessToken());

  protected readonly safeUrl = computed<SafeResourceUrl | null>(() => {
    if (this.isDocutechFlow()) {
      return null;
    }
    const url = this.esignUrl();
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  @HostListener('window:keyup', ['$event'])
  @HostListener('window:click', ['$event'])
  onUserActivity(event: Event): void {
    this.activityWatcher$.next(event);
  }

  @HostListener('window:message', ['$event'])
  onIframeMessage(event: MessageEvent): void {
    if (!this.embedHost || !event.origin.includes(this.embedHost)) {
      return;
    }

    this.activityWatcher$.next(event);

    const eventType = event.data?.type;

    switch (eventType) {
      case 'complete':
        this.onSigningComplete();
        break;
      case 'error':
        this.onSigningError(event.data?.error || 'An error occurred during signing');
        break;
      case 'signOut':
      case 'consentDeclined':
      case 'consentWithdrawn':
        this.onSigningCancelled(eventType);
        break;
      default:
    }
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.body.style.overflow = 'hidden';

    this.enterFullScreen();

    this.activityWatcher$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          interval(60 * 1000).pipe(
            take(30),
            tap(() => this.pingActivity()),
          ),
        ),
      )
      .subscribe();

    const url = this.esignUrl();
    if (url) {
      try {
        const parsedUrl = new URL(url);
        this.embedHost = parsedUrl.hostname;
      } catch (error) {
        this.loggingService.logException(error, '[ESignModal] Could not parse URL for origin validation');
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.platformService.isIOS()) {
      this.navigateInWebView();
      return;
    }

    const token = this.accessToken();
    if (token) {
      const form = this.esignForm();
      if (form) {
        const formEl = form.nativeElement;
        formEl.action = this.esignUrl();
        const tokenInput = formEl.querySelector('input[name="access_token"]') as HTMLInputElement;
        if (tokenInput) {
          tokenInput.value = token;
        }
        formEl.submit();
      } else {
        this.loggingService.logWarning('[ESignModal] Form element not found for DocuTech token POST');
        this.onIframeError();
      }
    }
  }

  private async navigateInWebView(): Promise<void> {
    const url = this.esignUrl();
    const token = this.accessToken();

    // Save return URL so the app navigates back here after esign (not main dashboard)
    localStorage.setItem('__ld_esign_return_url', window.location.pathname);

    // Disable status bar overlay so external page content isn't hidden behind the notch
    await this.statusBarService.setOverlaysWebView(false);

    if (token) {
      // Use form POST to send access_token (matches web iframe behavior)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'access_token';
      input.value = token;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
    } else {
      window.location.href = url;
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';

    this.exitFullScreen();

    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onIframeLoad(): void {
    this.isLoading.set(false);
  }

  protected onIframeError(): void {
    this.isLoading.set(false);
    this.hasError.set(true);
    this.errorMessage.set('Failed to load e-sign interface');
  }

  private onSigningComplete(): void {
    this.complete.emit();
  }

  private onSigningError(errorMsg: string): void {
    this.loggingService.logWarning(`[ESignModal] Signing error: ${errorMsg}`);
    this.hasError.set(true);
    this.errorMessage.set(errorMsg);
    this.error.emit(errorMsg);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onSigningCancelled(_reason: string): void {
    this.cancel.emit();
  }

  private pingActivity(): void {
    // Keep-alive ping — no telemetry needed
  }

  protected onClose(): void {
    this.cancel.emit();
  }

  private enterFullScreen(): void {
    const nav = document.querySelector('ld-mobile-bottom-nav') as HTMLElement;
    if (nav) {
      nav.style.display = 'none';
    }
    const contentArea = document.querySelector('.mobile-content-area') as HTMLElement;
    if (contentArea) {
      contentArea.style.bottom = '0';
      contentArea.style.overflow = 'hidden';
    }
  }

  private exitFullScreen(): void {
    const nav = document.querySelector('ld-mobile-bottom-nav') as HTMLElement;
    if (nav) {
      nav.style.display = '';
    }
    const contentArea = document.querySelector('.mobile-content-area') as HTMLElement;
    if (contentArea) {
      contentArea.style.bottom = '';
      contentArea.style.overflow = '';
    }
  }
}
